import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Scrollbars from "react-custom-scrollbars-2";
import { useSidebarData } from "../../data/json/sidebarData";
import { useDispatch, useSelector } from "react-redux";
import { resetAllMode } from "../../data/redux/themeSettingSlice";
import usePreviousRoute from "./usePreviousRoute";
import { RootState } from "../../../core/data/redux/store";

const Sidebar = () => {
  const location = useLocation();
  const previousLocation = usePreviousRoute();
  const dispatch = useDispatch();
  const sidebarData = useSidebarData();
  
  const authState = useSelector((state: RootState) => state.auth);
  const userRole = authState.roleName;
  
  // State to manage which submenus are open
  const [openSubmenus, setOpenSubmenus] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    const layoutPages = [
      "/layout-dark",
      "/layout-rtl", 
      "/layout-mini",
      "/layout-box",
      "/layout-default",
    ];
    const isCurrentLayout = layoutPages.some((path) =>
      location.pathname.includes(path)
    );
    const isPrevLayout =
      previousLocation &&
      layoutPages.some((path) => previousLocation.pathname.includes(path));

    if (isPrevLayout && !isCurrentLayout) {
      dispatch(resetAllMode());
    }
  }, [location, previousLocation, dispatch]);

  const isActive = (link: string | undefined): boolean =>
    !!link && (location.pathname === link || location.pathname.startsWith(link));

  // Check if any submenu item is active
  const isSubmenuActive = (subMenus: any[]): boolean => {
    return subMenus.some(subItem => isActive(subItem.link));
  };

  // Toggle submenu open/close
  const toggleSubmenu = (menuKey: string) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  if (!userRole) {
    console.log("No user role found, not rendering sidebar menu");
    return (
      <div className="sidebar" id="sidebar">
        <Scrollbars>
          <div className="sidebar-inner slimscroll">
            <div id="sidebar-menu" className="sidebar-menu">
              <ul>
                <li>No menu available</li>
              </ul>
            </div>
          </div>
        </Scrollbars>
      </div>
    );
  }

  return (
    <div className="sidebar" id="sidebar">
      <Scrollbars>
        <div className="sidebar-inner slimscroll">
          <div id="sidebar-menu" className="sidebar-menu">
            <ul>
              {sidebarData.filter((group: any) =>
                group.role.includes(userRole)
              ).map((group: any, index: number) => (
                <li key={index}>
                  <ul>
                    {group.submenuItems.map((item: any, i: number) => {
                      const menuKey = `${index}-${i}`;
                      const active = isActive(item.link);
                      const hasSubmenu = item.submenu && item.subMenus;
                      const submenuActive = hasSubmenu ? isSubmenuActive(item.subMenus) : false;
                      const isSubmenuOpen = openSubmenus[menuKey];

                      return (
                        <li key={i} className={`${active || submenuActive ? "active" : ""} ${hasSubmenu ? "submenu" : ""}`}>
                          {hasSubmenu ? (
                            <>
                              {/* Parent menu item with submenu */}
                              <a 
                                href="/report" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleSubmenu(menuKey);
                                }}
                                className={submenuActive ? "active" : ""}
                              >
                                <i className={item.icon}></i> 
                                <span>{item.label}</span>
                                <span>
                                  <i className={`fas ${isSubmenuOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                                </span>
                              </a>
                              
                              {/* Submenu items */}
                              <ul className={`submenu ${isSubmenuOpen ? 'submenu-open' : ''}`} style={{
                                display: isSubmenuOpen ? 'block' : 'none'
                              }}>
                                {item.subMenus.map((subItem: any, subIndex: number) => {
                                  const subActive = isActive(subItem.link);
                                  return (
                                    <li key={subIndex} className={subActive ? "active" : ""}>
                                      <Link to={subItem.link}>
                                        <i className={subItem.icon}></i>
                                        <span>{subItem.label}</span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                          ) : (
                            /* Regular menu item without submenu */
                            <Link to={item.link}>
                              <i className={item.icon}></i>
                              <span>{item.label}</span>
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

export default Sidebar;